import React, { useState, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, Search, CircleAlert as AlertCircle } from 'lucide-react';
import InputMask from 'react-input-mask-next';
import { useClientRatings } from '../hooks/useClientRatings';
import { ClientRatingBadge } from '../components/ClientRatingBadge';

interface Client {
  id: string;
  client_name: string;
  cpf: string;
  phone: string;
  relative_phones: { numero: string; relacao: string }[];
  email: string;
  address: string;
  business_address: string;
  pix_key: string;
  code: string;
  seller: {
    email: string;
  } | null;
  contract_files: {
    id: string;
    file_name: string;
    file_url: string;
    file_type: string;
  }[];
  contracts: {
    id: string;
    loan_amount: number;
    total_amount: number;
    created_at: string;
    payment_status: string;
    ruta: number;
    installments_data: {
      id: string;
      due_date: string;
      amount: number;
      status: string;
    }[];
  }[];
  overall_status: 'active' | 'inactive';
}

export default function Clients() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [nameSearchTerm, setNameSearchTerm] = useState('');
  const [cpfSearchTerm, setCpfSearchTerm] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchClients = async () => {
    if (!user) return;
    
    // Validate that at least one search term is provided
    if (!nameSearchTerm.trim() && !cpfSearchTerm.trim()) {
      setError('Por favor, digite pelo menos um filtro (nome ou CPF) para buscar clientes.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      let query = supabase
        .from('contracts')
        .select(`
          id,
          client_name,
          cpf,
          phone,
          relative_phones,
          email,
          address,
          business_address,
          pix_key,
          code,
          seller:seller_id(email),
          contract_files(*),
          loan_amount,
          total_amount,
          created_at,
          payment_status,
          ruta,
          installments_data:installments(*)
        `)
        .eq('seller_id', user?.id)
        .eq('unit_id', user?.unit_id)
        .eq('approval_status', 'approved')
        .order('created_at', { ascending: false });

      // Apply filters
      if (nameSearchTerm.trim()) {
        query = query.ilike('client_name', `%${nameSearchTerm.trim()}%`);
      }
      
      if (cpfSearchTerm.trim()) {
        const cleanCpf = cpfSearchTerm.replace(/\D/g, '');
        query = query.ilike('cpf', `%${cleanCpf}%`);
      }

      const { data: contracts, error: contractsError } = await query;

      if (contractsError) throw contractsError;

      // Group contracts by client (using CPF as unique identifier)
      const clientsMap = new Map<string, Client>();
      
      contracts?.forEach(contract => {
        const existingClient = clientsMap.get(contract.cpf);
        
        if (existingClient) {
          existingClient.contracts.push({
            id: contract.id,
            loan_amount: contract.loan_amount,
            total_amount: contract.total_amount,
            created_at: contract.created_at,
            payment_status: contract.payment_status,
            ruta: contract.ruta,
            installments_data: contract.installments_data
          });
          
          // Add contract files from this contract to the existing client
          if (contract.contract_files && contract.contract_files.length > 0) {
            existingClient.contract_files.push(...contract.contract_files);
          }
        } else {
          clientsMap.set(contract.cpf, {
            id: contract.id,
            client_name: contract.client_name,
            cpf: contract.cpf,
            phone: contract.phone,
            relative_phones: contract.relative_phones,
            email: contract.email,
            address: contract.address,
            business_address: contract.business_address,
            pix_key: contract.pix_key,
            code: contract.code,
            seller: contract.seller,
            contract_files: contract.contract_files || [],
            overall_status: 'inactive', // Default to inactive, will be updated
            contracts: [{
              id: contract.id,
              loan_amount: contract.loan_amount,
              total_amount: contract.total_amount,
              created_at: contract.created_at,
              payment_status: contract.payment_status,
              ruta: contract.ruta,
              installments_data: contract.installments_data
            }]
          });
        }
      });

      // Deduplicate contract files for each client
      clientsMap.forEach((client) => {
        const uniqueFiles = new Map();
        client.contract_files.forEach(file => {
          uniqueFiles.set(file.id, file);
        });
        client.contract_files = Array.from(uniqueFiles.values());
      });

      // Post-processing to determine overall_status for each client
      clientsMap.forEach((client, cpf) => {
        const hasAnyPending = client.contracts.some(contract =>
          contract.installments_data.some(
            (installment: any) => installment.status !== 'paid' || (installment.paid_amount || 0) < installment.amount
          )
        );
        client.overall_status = hasAnyPending ? 'active' : 'inactive';
      });

      const clientsList = Array.from(clientsMap.values());
      setClients(clientsList);
      setSearched(true);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchClients();
  };

  const handleNewContract = (client: Client) => {
    // Navigate to loan simulation with client data
    navigate('/dashboard/loan-simulation', {
      state: {
        existingClient: {
          name: client.client_name,
          cpf: client.cpf,
          phone: client.phone,
          relativePhones: client.relative_phones,
          email: client.email,
          address: client.address,
          businessAddress: client.business_address,
          pixKey: client.pix_key,
          code: client.code
        }
      }
    });
  };

  const handleCopyClientData = (client: Client) => {
    const clientData = `
Dados do Cliente:
Nome: ${client.client_name}
CPF: ${client.cpf}
Telefone: ${client.phone}

Telefones dos Parentes:
${client.relative_phones.map(phone => `${phone.relacao}: ${phone.numero}`).join('\n')}

Endereço Residencial: ${client.address}
Endereço Comercial: ${client.business_address}

${client.contracts.map(contract => `
Valor do Empréstimo: ${formatCurrency(contract.loan_amount)}
Valor Total: ${formatCurrency(contract.total_amount)}
Data: ${new Date(contract.created_at).toLocaleDateString('pt-BR')}
Ruta: ${contract.ruta}`).join('\n')}`.trim();

    navigator.clipboard.writeText(clientData);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatCPF = (cpf: string) => {
    const numbers = cpf.replace(/\D/g, '');
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatPhone = (phone: string) => {
    const numbers = phone.replace(/\D/g, '');
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const clientIds = useMemo(() =>
    clients.map(client => client.id),
    [clients]
  );

  const { ratings } = useClientRatings(clientIds);

  return (
    <div className="min-h-screen bg-gray-50 p-4 lg:p-0">
      <div className="max-w-7xl mx-auto space-y-4 lg:space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl lg:text-2xl font-bold text-gray-800">Clientes</h1>
      </div>

      {/* Search Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 lg:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Search className="w-5 h-5 text-blue-600" />
          <h2 className="text-base lg:text-lg font-semibold text-gray-800">Buscar Clientes</h2>
        </div>
        
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome do Cliente
              </label>
              <input
                type="text"
                value={nameSearchTerm}
                onChange={(e) => setNameSearchTerm(e.target.value)}
                placeholder="Digite o nome do cliente..."
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm lg:text-base"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CPF
              </label>
              <InputMask
                mask="999.999.999-99"
                value={cpfSearchTerm}
                onChange={(e) => setCpfSearchTerm(e.target.value)}
                placeholder="000.000.000-00"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm lg:text-base"
              />
            </div>
          </div>

          <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-4">
            <button
              type="submit"
              disabled={loading || (!nameSearchTerm.trim() && !cpfSearchTerm.trim())}
              className={`flex items-center justify-center gap-2 px-6 py-2 rounded-md text-white transition-colors text-sm lg:text-base ${
                loading || (!nameSearchTerm.trim() && !cpfSearchTerm.trim())
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Buscando...</span>
                </>
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  <span>Buscar Clientes</span>
                </>
              )}
            </button>
            
            {(nameSearchTerm.trim() || cpfSearchTerm.trim()) && (
              <button
                type="button"
                onClick={() => {
                  setNameSearchTerm('');
                  setCpfSearchTerm('');
                  setClients([]);
                  setSearched(false);
                  setError(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm lg:text-base"
              >
                Limpar Filtros
              </button>
            )}
          </div>
        </form>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700">{error}</p>
          </div>
        )}
      </div>

      {/* Results Section */}
      <div className="bg-white rounded-lg shadow-md p-4 lg:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Buscando clientes...</span>
          </div>
        ) : !searched ? (
          <div className="text-center py-12">
            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-base lg:text-lg font-medium text-gray-900 mb-2">Busque por clientes</h3>
            <p className="text-gray-500">
              Use os filtros acima para encontrar clientes pelo nome ou CPF.
              <br />
              Digite pelo menos um dos campos para iniciar a busca.
            </p>
          </div>
        ) : clients.length === 0 ? (
          <div className="text-center py-12">
            <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-base lg:text-lg font-medium text-gray-900 mb-2">Nenhum cliente encontrado</h3>
            <p className="text-gray-500">
              Não foram encontrados clientes com os filtros aplicados.
              <br />
              Tente ajustar os termos de busca.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base lg:text-lg font-semibold text-gray-800">
                Resultados da Busca ({clients.length} cliente{clients.length !== 1 ? 's' : ''})
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
              {clients.map((client) => (
                <div key={client.id} className="bg-gray-50 rounded-lg p-4 lg:p-6 border border-gray-200">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-base lg:text-lg font-semibold text-gray-900">{client.client_name}</h3>
                        <ClientRatingBadge rating={ratings.get(client.id) || null} />
                      </div>
                      <p className="text-sm text-gray-600">Código: {client.code}</p>
                      <p className="text-sm text-gray-600">CPF: {formatCPF(client.cpf)}</p>
                      <p className="text-sm text-gray-600">Telefone: {formatPhone(client.phone)}</p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        client.overall_status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {client.overall_status === 'active' ? 'Ativo' : 'Inativo'}
                      </span>
                      <button
                        onClick={() => handleNewContract(client)}
                        className="flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Novo</span>
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Contratos:</h4>
                      <div className="space-y-2">
                        {client.contracts.map((contract) => (
                          <div
                            key={contract.id}
                            className="flex flex-col lg:flex-row justify-between items-start lg:items-center p-2 bg-white rounded-md border border-gray-200 gap-2 lg:gap-0"
                          >
                            <div>
                              <p className="text-sm font-medium">
                                {formatCurrency(contract.loan_amount)}
                              </p>
                              <p className="text-xs text-gray-500">
                                {new Date(contract.created_at).toLocaleDateString('pt-BR')} • Ruta {contract.ruta}
                              </p>
                            </div>
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                              Pago
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Contatos:</h4>
                      <div className="space-y-1">
                        {client.relative_phones.slice(0, 2).map((phone, index) => (
                          <p key={index} className="text-sm text-gray-600">
                            {phone.relacao}: {formatPhone(phone.numero)}
                          </p>
                        ))}
                        {client.relative_phones.length > 2 && (
                          <p className="text-xs text-gray-500">
                            +{client.relative_phones.length - 2} mais...
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
